import React, {useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";

import {
  setAiRenderOptions,
  setAiFeatureAreas,
  setAiFeatureAreasUserModified,
  setDefaultAiFeatureAreasGroups,
} from "../aiActions";
import {useAppDispatch, useAppSelector} from "../../../storeHooks";
import {getCommonVisualizerType} from "../../../config";
import {getCurrentLayout} from "../../appLayout";
import {getActivePartIdsForFeatureArea} from "../aiFunctions";
import {FEATURE_AREAS} from "../aiFeatureAreasConstants";
import UiIcon from "../../ui/icons/uiIcon";
import UiPopupAIContentFeaturesAreaDropdown from "./UiPopupAIContentFeaturesAreaDropdown";

export default function UiPopupAIContentFeaturesAreas() {

  const aiRenderOptions = useAppSelector(state => state.aiState.aiRenderOptions);
  const aiFeatureAreas = useAppSelector(state => state.aiState.aiFeatureAreas);
  const fabricatorData = useAppSelector(state => state.fabricatorState);

  const {aiFeatureAreaEnabled} = aiRenderOptions;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const visualizerType = getCommonVisualizerType();

  // Refresh the default groups every time this popup is opened (e.g. canvas may have
  // changed since it was last shown) - setDefaultAiFeatureAreasGroups no-ops on its own
  // once the user has directly edited aiFeatureAreas.
  useEffect(() => {
    if (aiFeatureAreaEnabled) {
      dispatch(setDefaultAiFeatureAreasGroups());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (visualizerType !== "bathroom") {
    return null;
  }

  const layout = getCurrentLayout();

  const toggleFeatureArea = (featureArea: string, availablePartIds: string[]) => {
    const isSelected = !!aiFeatureAreas?.[featureArea]?.length;
    const currentActivePartIds = getActivePartIdsForFeatureArea(availablePartIds, layout);
    dispatch(setAiFeatureAreasUserModified(true));
    dispatch(setAiFeatureAreas({
      [featureArea]: isSelected ? [] : currentActivePartIds,
    }));
  };

  const toggleFeatureGroup = (featureArea: string, partId: string) => {
    const selectedPartIds: string[] = aiFeatureAreas?.[featureArea] || [];
    dispatch(setAiFeatureAreasUserModified(true));
    dispatch(setAiFeatureAreas({
      [featureArea]: selectedPartIds.includes(partId)
        ? selectedPartIds.filter(id => id !== partId)
        : [...selectedPartIds, partId],
    }));
  };

  return (
    <div className="u-mb-1 flex flex--top flex--start u-gap-1">
      <div className={"flex flex--column flex--start flex--top"}>
        <span className="ui-radio-label nowrap">Attach Additional Areas:</span>
        <button className={"ui-radio ui-radio--small" + (aiFeatureAreaEnabled ? " ui-radio--checked" : "")}
                onClick={() => dispatch(setAiRenderOptions({
                  "aiFeatureAreaEnabled": !aiFeatureAreaEnabled}
                ))}>
          <span className="ui-radio-circle"/>
          <span className="ui-radio-text ui-radio-text--yes"><FormattedMessage id="radioButton.on"/></span>
          <span className="ui-radio-text ui-radio-text--no"><FormattedMessage id="radioButton.off"/></span>
        </button>
      </div>
      {aiFeatureAreaEnabled ? (
        <div className="flex flex--start flex--top u-gap-1">
          {FEATURE_AREAS.map(({featureArea, partIds}) => {
            const featureGroupItems = partIds.map(partId => {
              return {partId, featureGroup: layout?.getFeatureGroupByPartId(partId)}
            }).filter(fg=> fg.featureGroup);
            const isSelected = !!aiFeatureAreas?.[featureArea]?.length;
            return (
              <div key={featureArea} className="flex flex--column flex--start flex--top u-gap-025 min-w-[132px]">
                <div className="flex flex--center flex--start">
                  <div className={"ui-checkbox ui-checkbox--small"
                    + (isSelected ? " ui-checkbox--checked-svg" : "")}
                       onClick={() => toggleFeatureArea(featureArea, featureGroupItems.map(item => item.partId))}>
                    <div className="ui-checkbox__svg">
                      <UiIcon icon="checkmark"/>
                    </div>
                  </div>
                  <div className="ui-checkbox__label"
                       onClick={() => toggleFeatureArea(featureArea, featureGroupItems.map(item => item.partId))}>
                    <span className="color-primary bold">{featureArea}</span>
                  </div>
                </div>
                {isSelected ? (
                  <UiPopupAIContentFeaturesAreaDropdown
                    featureArea={featureArea}
                    featureGroupItems={featureGroupItems}
                    selectedPartIds={aiFeatureAreas?.[featureArea] || []}
                    onToggle={toggleFeatureGroup}
                    intl={intl}
                    fabricatorData={fabricatorData}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  )
}
